import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";

export interface AccessRequest {
  id: number;
  email: string;
  display_name: string;
  reason: string | null;
  status: string;
  created_at: string;
  decided_at: string | null;
  denial_reason: string | null;
}

export interface AdminUser {
  id: number;
  email: string;
  display_name: string | null;
  role: string;
  status: string;
  created_at: string;
  last_login_at: string | null;
  can_view_agents: boolean;
}

export const adminKeys = {
  accessRequests: (status: string) => ["admin", "access-requests", status] as const,
  users: ["admin", "users"] as const,
};

export function useAccessRequests(statusFilter = "pending") {
  return useQuery<AccessRequest[]>({
    queryKey: adminKeys.accessRequests(statusFilter),
    queryFn: () =>
      api
        .get("/admin/access-requests", { params: { status_filter: statusFilter } })
        .then((r) => r.data),
  });
}

export function useApproveRequest() {
  const qc = useQueryClient();
  return useMutation<AdminUser, Error, number>({
    mutationFn: (id) =>
      api.post(`/admin/access-requests/${id}/approve`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "access-requests"] });
      qc.invalidateQueries({ queryKey: adminKeys.users });
    },
  });
}

export function useDenyRequest() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (id) =>
      api.post(`/admin/access-requests/${id}/deny`, {}).then(() => undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "access-requests"] });
    },
  });
}

export function useAdminUsers() {
  return useQuery<AdminUser[]>({
    queryKey: adminKeys.users,
    queryFn: () => api.get("/admin/users").then((r) => r.data),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation<
    AdminUser,
    Error,
    { id: number; role?: string; status?: string; can_view_agents?: boolean }
  >({
    mutationFn: ({ id, role, status }) =>
      api.patch(`/admin/users/${id}`, { role, status }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.users });
    },
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (id) => api.delete(`/admin/users/${id}`).then(() => undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.users });
    },
  });
}

export interface CreatedUser {
  user: AdminUser;
  invite_sent: boolean;
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation<
    CreatedUser,
    Error,
    { email: string; display_name: string; role: string; send_invite: boolean }
  >({
    mutationFn: (body) => api.post("/admin/users", body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.users });
    },
  });
}


/** Get-them-in-now actions (2026-10-12): a fresh sign-in link to paste
 *  anywhere, or a one-time temporary password to hand over directly. */
export function useInviteLink() {
  return useMutation<{ link: string; expires_in_hours: number; email_sent: boolean }, Error, number>({
    mutationFn: (id) => api.post(`/admin/users/${id}/invite-link`).then((r) => r.data),
  });
}

export function useTemporaryPassword() {
  return useMutation<{ email: string; password: string }, Error, number>({
    mutationFn: (id) => api.post(`/admin/users/${id}/temporary-password`).then((r) => r.data),
  });
}
